import { create } from 'zustand';

import { STORAGE_KEYS } from '@/constants/storageKeys';
import { getPlatformStorage, safeStorageOp } from '@/util/platformStorage';

type InboxQuestionGroupCardsOptInState = {
  optedIn: boolean;
  /** False until the stored choice has been read, which counts as off. */
  hydrated: boolean;
  hydrate: () => Promise<void>;
  setOptedIn: (value: boolean) => void;
};

const STORED_ON = '1';

/**
 * A staff member's own choice, kept on this device, to see the nested inbox.
 *
 * It only widens the flag. The Primum group check stays mandatory wherever this is read, so a value
 * left in storage by someone who later loses staff access shows them nothing. A read that has not
 * finished, or that fails, leaves it off so the list starts flat.
 */
export const useInboxQuestionGroupCardsOptInStore = create<InboxQuestionGroupCardsOptInState>(
  (set, get) => ({
    optedIn: false,
    hydrated: false,
    hydrate: async () => {
      if (get().hydrated) {
        return;
      }

      const stored = await safeStorageOp(
        () => getPlatformStorage().getItem(STORAGE_KEYS.INBOX_QUESTION_GROUP_CARDS_OPT_IN),
        { component: 'inboxQuestionGroupCardsOptInStore', action: 'read' }
      );

      set({ optedIn: stored === STORED_ON, hydrated: true });
    },
    setOptedIn: (value) => {
      set({ optedIn: value, hydrated: true });

      safeStorageOp(
        () =>
          value
            ? getPlatformStorage().setItem(
                STORAGE_KEYS.INBOX_QUESTION_GROUP_CARDS_OPT_IN,
                STORED_ON
              )
            : getPlatformStorage().removeItem(STORAGE_KEYS.INBOX_QUESTION_GROUP_CARDS_OPT_IN),
        { component: 'inboxQuestionGroupCardsOptInStore', action: 'write' }
      ).catch(() => undefined);
    },
  })
);

useInboxQuestionGroupCardsOptInStore
  .getState()
  .hydrate()
  .catch(() => undefined);

/** The opt-in as a reader of the gate should see it: off until the stored value is known. */
export function useInboxQuestionGroupCardsOptIn() {
  return useInboxQuestionGroupCardsOptInStore((state) => state.hydrated && state.optedIn);
}
